/* eslint-disable no-underscore-dangle */
import React, { useState, useEffect, useRef } from "react";
import Loader from "../../../common/loader/Loader";
import ModalDefault from "../../../common/modal/modal-default";
import UserActivityForm from "./user-activity-form";
import UserList from "./user-list";
import WidgetEmpty from "../empty-widget-content/empty-widget-content";
import ActionGroupService from "../../../../services/action-group-service";
import UserService from "../../../../services/user-service";
import { NUMBER_OF_ITEMS_OPTIONS } from "../widget-constants";
import { shutdownFeed } from "../widget-helpers";
import isEmpty from "lodash/isEmpty";
import WidgetHeader from "../widget-header/WidgetHeader";

const INITIAL_VALUES = {
  feed: null,
};

const UserActivity = (props) => {
  const [isSettingsModalShown, setIsSettingsModalShown] = useState(false);
  const [usersFeed, setUsersFeed] = useState(INITIAL_VALUES.feed);
  const [formData, setFormData] = useState({
    title: props.data.title,
    numberOfItems:
      props.data.config.numberOfItems || NUMBER_OF_ITEMS_OPTIONS[2],
    organizations: props.data.config.organizations || [],
    roles: props.data.config.roles || [],
  });
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const componentIsMounted = useRef(false);
  const latestFeedRef = useRef(null);
  const authorizedTenants = useRef({});
  const rolesOptionsRef = useRef([]);

  const getUserRoleLabel = (user) => {
    const label = UserService.getUserRoleLabel(user);
    if (!label) {
      const roleId = user._pristineProperties.actionGroupId;
      const role = rolesOptionsRef.current.find((r) => r.id === roleId);
      return role ? role.label : "";
    }
    return label;
  };

  const getUserOrganizationLabel = (user) => {
    const lastTenantId = user._pristineProperties.lastTenantId;
    return authorizedTenants.current[lastTenantId]?.name;
  };

  const loadRolesOptions = async () => {
    const roles = await ActionGroupService.getRolesForUserOrganizations();
    roles.sort((roleA, roleB) => {
      return roleA.label.toLowerCase() < roleB.label.toLowerCase() ? -1 : 1;
    });
    if (componentIsMounted.current) {
      rolesOptionsRef.current = roles;
    }
  };

  const loadAuthorizedTenants = () => {
    const authTenants = UserService.getAuthorizedTenants();
    if (componentIsMounted.current) {
      authorizedTenants.current = authTenants;
    }
  };

  const removeFeedListeners = (feed) => {
    if (feed && typeof feed.removeAllListeners === "function") {
      feed.removeAllListeners("EntityPropertiesUpdated");
      feed.removeAllListeners("EntityAdded");
      feed.removeAllListeners("EntityRemoved");
    }
  };

  useEffect(() => {
    loadRolesOptions();
    componentIsMounted.current = true;
    loadAuthorizedTenants();
    return () => {
      componentIsMounted.current = false;
      removeFeedListeners(latestFeedRef.current);
      shutdownFeed(latestFeedRef.current);
    };
  }, []);

  useEffect(() => {
    const onFeedPropertiesUpdated = (newFeed) => {
      newFeed._pristineProperties.role = getUserRoleLabel(newFeed);
      newFeed._pristineProperties.lastSolution =
        UserService.getLastSolutionForUser(newFeed);
      if (componentIsMounted.current) {
        setUsersFeed((usersFeed) => ({
          ...usersFeed,
          _cache: {
            ...usersFeed._cache,
            [newFeed.id]: newFeed,
          },
        }));
      }
    };

    const onEntityAdded = (newFeed) => {
      if (componentIsMounted.current) {
        setUsersFeed((usersFeed) => ({
          ...usersFeed,
          _cache: {
            ...usersFeed._cache,
            [newFeed.id]: newFeed,
          },
        }));
      }
    };

    const onEntityRemoved = (oldFeed) => {
      if (componentIsMounted.current) {
        setUsersFeed((usersFeed) => ({
          ...usersFeed,
          _cache: {
            ...usersFeed._cache,
            [oldFeed.id]: undefined,
          },
        }));
      }
    };
    const addFeedListeners = (feed) => {
      if (feed) {
        feed.on("EntityPropertiesUpdated", onFeedPropertiesUpdated);
        feed.on("EntityAdded", onEntityAdded);
        feed.on("EntityRemoved", onEntityRemoved);
      }
    };

    const addUserOrganizationProperty = (usersData) => {
      Object.values(usersData._cache).forEach((user) => {
        user._pristineProperties.organization = getUserOrganizationLabel(user);
      });
    };

    const addUserRoleProperty = (usersData) => {
      Object.values(usersData._cache).forEach((user) => {
        user._pristineProperties.role = getUserRoleLabel(user);
        user._pristineProperties.lastSolution =
          UserService.getLastSolutionForUser(user);
      });
    };

    const loadUserFeed = async () => {
      setIsLoadingData(true);
      let feed = INITIAL_VALUES.feed;
      let errorMessage = "";
      if (latestFeedRef.current) {
        removeFeedListeners(latestFeedRef.current);
        shutdownFeed(latestFeedRef.current);
      }
      try {
        feed = await UserService.getUsersFeed(formData);
        addUserRoleProperty(feed);
        addFeedListeners(feed);
        addUserOrganizationProperty(feed);
      } catch (err) {
        errorMessage = err.errorMsg;
      }
      if (componentIsMounted.current) {
        latestFeedRef.current = feed;
        setUsersFeed(feed);

        setIsLoadingData(false);
        setErrorMessage(errorMessage);
      }
    };
    loadUserFeed();
  }, [formData]);

  const toggleSettingsModal = (e) => {
    e.stopPropagation();
    setIsSettingsModalShown((isShown) => !isShown);
  };

  const onFormSubmit = (values) => {
    setIsSettingsModalShown(false);
    setFormData(values);
    props.onUpdate({
      ...props.data,
      title: values.title,
      config: {
        ...props.data.config,
        ...values,
      },
    });
  };

  const getContentType = () => {
    if (errorMessage) {
      return "error";
    }
    if (
      usersFeed &&
      !isEmpty(usersFeed) &&
      Object.keys(usersFeed._cache).length > 0
    ) {
      return "users";
    }
    return "empty";
  };

  const renderContent = () => {
    switch (getContentType()) {
      case "error":
        return (
          <WidgetEmpty
            isEditAllowed={props.isEditAllowed}
            showSettingsModal={toggleSettingsModal}
            text={errorMessage || "Something went wrong."}
          />
        );
      case "empty":
        return (
          <WidgetEmpty
            isEditAllowed={props.isEditAllowed}
            showSettingsModal={toggleSettingsModal}
            text="No users found. Please reconfigure the parameters"
          />
        );
      case "users":
        return (
          <UserList
            usersFeed={usersFeed}
            numberOfItemsToShow={formData.numberOfItems.value}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div className="glu-dashboard-card glu-content">
        {isLoadingData && <Loader />}
        <WidgetHeader
          isEditAllowed={props.isEditAllowed}
          title={formData.title}
          height={props.height}
          width={props.width}
          showWidgetInfo={props.showWidgetInfo(props.data)}
          openConfiguration={toggleSettingsModal}
          onRemove={props.onRemove(props.data)}
          onClone={props.onClone(props.data)}
        />
        <div className="glu-content__body">{renderContent()}</div>
      </div>
      {isSettingsModalShown && (
        <ModalDefault isOpen={isSettingsModalShown}>
          <div className="glu-modal-content">
            <div className="glu-modal__header">
              <div className="glu-modal__title glu-flex-grow">
                {props.data.title}
              </div>
              <div className="glu-flex-shrink">
                <button
                  aria-label="close"
                  type="button"
                  className="glu-btn-link g-icon g-icon-close"
                  onClick={toggleSettingsModal}
                />
              </div>
            </div>
            <UserActivityForm
              initialValues={formData}
              onCancel={toggleSettingsModal}
              countOptions={NUMBER_OF_ITEMS_OPTIONS}
              onFormSubmit={onFormSubmit}
              rolesOptions={rolesOptionsRef.current}
            />
          </div>
        </ModalDefault>
      )}
    </>
  );
};

export default UserActivity;
